import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { login } from '../../services/api';

// Timeout duration in milliseconds
const REQUEST_TIMEOUT = 15000; // 15 seconds

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState<'client' | 'barber'>('client');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Create a promise that rejects after the timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), REQUEST_TIMEOUT);
    });

    try {
      // Race the login request against the timeout
      const data = await Promise.race([
        login(email, password, userType),
        timeoutPromise
      ]) as any;
      
      authLogin(data.access_token, userType, data.user_id);
      navigate(userType === 'client' ? '/client' : '/barber');
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof Error && error.message === 'Request timed out') {
        setError(t('auth.errors.timeout'));
      } else {
        setError(t('auth.errors.invalid'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserTypeChange = (event: SelectChangeEvent) => {
    setUserType(event.target.value as 'client' | 'barber');
  };

  return (
    <Container maxWidth="sm">
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mt: 8,
          background: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <Typography 
          variant="h4" 
          align="center" 
          gutterBottom
          sx={{ color: 'text.primary', fontWeight: 500 }}
        >
          {t('auth.login')}
        </Typography>
        {error && (
          <Typography 
            color="error" 
            align="center" 
            gutterBottom
            sx={{ 
              color: 'error.main',
              backgroundColor: 'error.light',
              p: 1,
              borderRadius: 1
            }}
          >
            {error}
          </Typography>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <FormControl fullWidth margin="normal" disabled={loading}>
            <InputLabel sx={{ color: 'text.secondary' }}>{t('auth.userType')}</InputLabel>
            <Select
              value={userType}
              onChange={handleUserTypeChange}
              label={t('auth.userType')}
              sx={{
                '& .MuiSelect-select': { color: 'text.primary' },
              }}
            >
              <MenuItem value="client">{t('auth.client')}</MenuItem>
              <MenuItem value="barber">{t('auth.barber')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: 'primary.main',
                color: 'text.light',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                position: 'relative',
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  {t('auth.loggingIn')}
                </Box>
              ) : (
                t('auth.login')
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Login; 